import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import TextArea from '@/common/components/TextArea'
import { ErrorResponse } from '@/common/types/errorResponse'
import { handleServerError } from '@/common/utils/handleServerError'
import { useReviewBillingSupplyAuthorization } from '@/features/supplyAuthorizations/hooks/useReviewBillingSupplyAuthorization'
import {
  rejectBillingSupplyAuthorizationSchema,
  RejectBillingSupplyAuthorizationValidationSchema,
} from '@/features/supplyAuthorizations/schemas/reject.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface RejectBillingSupplyAuthorizationModalProps {
  disabled?: boolean
}

export const RejectBillingSupplyAuthorizationModal = ({ disabled }: RejectBillingSupplyAuthorizationModalProps) => {
  const { id } = useParams()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [baseError, setBaseError] = useState('')

  const {
    register,
    handleSubmit,
    reset: resetForm,
    setError,
    formState: { errors },
  } = useForm<RejectBillingSupplyAuthorizationValidationSchema>({
    resolver: zodResolver(rejectBillingSupplyAuthorizationSchema),
    defaultValues: {
      reject_reason: '',
    },
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['billingSupplyAuthorizations'] })
    queryClient.invalidateQueries({ queryKey: ['billingSupplyAuthorization', id] })
    resetForm()
    setIsOpen(false)
  }

  const onError = (error: ErrorResponse) => {
    handleServerError({ error, setBaseError, setError })
  }

  const { mutate: rejectSupplyAuthorization, isPending: rejectSupplyAuthorizationIsPending } =
    useReviewBillingSupplyAuthorization({
      supplyAuthorizationId: id,
      review: 'reject',
      onSuccess,
      onError,
    })

  const onSubmit: SubmitHandler<RejectBillingSupplyAuthorizationValidationSchema> = (data) => {
    setBaseError('')
    rejectSupplyAuthorization(data)
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          type="button"
          variant="yellow"
          className="w-[10.25rem]"
          disabled={disabled}
          operationKlassToCheckPermission="Admin::Billing::SupplyAuthorizations::Reject"
        >
          Declinar
        </Button>
      }
      triggerDisabled={disabled}
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={rejectSupplyAuthorizationIsPending}
      title="Você tem certeza?"
      titleClass="text-2xl font-bold mb-2"
      subtitle="Essa autorização de fornecimento será cancelada."
      body={
        <form className="flex flex-col gap-4">
          {baseError && <p className="text-sm text-danger-soft">{baseError}</p>}
          <TextArea
            label="Justificativa"
            className="text-white-800"
            placeholder="Escreva aqui sua justificativa"
            errorMessage={errors?.reject_reason?.message}
            {...register('reject_reason')}
            markAsRequired
          />
        </form>
      }
    />
  )
}
